import { z } from "zod";
import { USER_ROLES } from "../rbac/roles";

/** Schema for the login form. Validates email format and non-empty password. */
export const loginSchema = z.object({
  email: z.string().email("Invalid email address").max(255, "Email must be 255 characters or less"),
  password: z.string().min(1, "Password is required").max(128, "Password is too long"),
});

/** Schema for the registration form. Requires name, email, and password with minimum length. */
export const registerSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
  email: z.string().email("Invalid email address").max(255, "Email must be 255 characters or less"),
  password: z
    .string()
    .min(12, "Password must be at least 12 characters")
    .max(128, "Password is too long"),
});

/** Schema for the profile setup form. Requires a name. */
export const profileSetupSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name must be 100 characters or less"),
});

/**
 * Admin zakládá uživatele z aplikace (invite-only model — veřejná registrace
 * je vypnutá). Heslo nezadává admin: uživateli přijde pozvánka. `roles` jsou
 * doplňkové role navíc; `appAccess=false` = jen evidence (bez pozvánky/přístupu).
 */
export const adminCreateUserSchema = z.object({
  name: z.string().min(1, "Jméno je povinné").max(100, "Jméno musí být 100 znaků nebo méně"),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Neplatný e-mail")
    .max(255, "E-mail musí být 255 znaků nebo méně"),
  role: z.enum(USER_ROLES),
  roles: z.array(z.enum(USER_ROLES)).max(USER_ROLES.length, "Příliš mnoho rolí").default([]),
  appAccess: z.boolean().default(true),
});

/**
 * Schema for the self-service "change password" form. `newPassword` follows
 * the Better Auth minimum (12 chars). Confirmation must match.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, "Zadejte stávající heslo")
      .max(128, "Heslo je příliš dlouhé"),
    newPassword: z
      .string()
      .min(12, "Nové heslo musí mít alespoň 12 znaků")
      .max(128, "Heslo je příliš dlouhé"),
    confirmPassword: z.string().min(1, "Potvrďte nové heslo").max(128, "Heslo je příliš dlouhé"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Hesla se neshodují",
    path: ["confirmPassword"],
  });

/** Inferred type for login form input values. */
export type LoginInput = z.infer<typeof loginSchema>;

/** Inferred type for registration form input values. */
export type RegisterInput = z.infer<typeof registerSchema>;

/** Inferred type for profile setup form input values. */
export type ProfileSetupInput = z.infer<typeof profileSetupSchema>;

/** „Zapomenuté heslo" — uživatel zadá e-mail, přijde mu odkaz. */
export const requestPasswordResetSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Neplatný e-mail")
    .max(255, "E-mail musí být 255 znaků nebo méně"),
});

/** Nastavení nového hesla z tokenu (pozvánka i reset). */
export const resetPasswordFormSchema = z
  .object({
    newPassword: z
      .string()
      .min(12, "Heslo musí mít alespoň 12 znaků")
      .max(128, "Heslo je příliš dlouhé"),
    confirmPassword: z.string().min(1, "Potvrďte heslo").max(128, "Heslo je příliš dlouhé"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Hesla se neshodují",
    path: ["confirmPassword"],
  });

/** Admin upravuje jméno, e-mail a doplňkové role existujícího uživatele. */
export const adminUpdateUserSchema = z.object({
  userId: z.string().min(1, "Chybí ID uživatele").max(255, "Neplatné ID uživatele"),
  name: z.string().min(1, "Jméno je povinné").max(100, "Jméno musí být 100 znaků nebo méně"),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Neplatný e-mail")
    .max(255, "E-mail musí být 255 znaků nebo méně"),
  roles: z.array(z.enum(USER_ROLES)).max(USER_ROLES.length, "Příliš mnoho rolí").default([]),
});

/** Admin přepíná přístup uživatele do aplikace (evidence ↔ plný přístup). */
export const setAppAccessSchema = z.object({
  userId: z.string().min(1, "Chybí ID uživatele").max(255, "Neplatné ID uživatele"),
  appAccess: z.boolean(),
});

/** Inferred type for the change-password form input values. */
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** Inferred type for the admin create-user form input values. */
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;

/** Inferred type for the request-password-reset form input values. */
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetSchema>;

/** Inferred type for the reset-password form input values. */
export type ResetPasswordFormInput = z.infer<typeof resetPasswordFormSchema>;

/** Inferred type for the admin update-user form input values. */
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

/** Inferred type for the set-app-access input values. */
export type SetAppAccessInput = z.infer<typeof setAppAccessSchema>;
